import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { contactInfo } from "../data";
import ContactForm from "../components/ContactForm";

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Tjek om der blev sendt et emne med fra en "Book tid" knap
  const initialSubject = location.state?.subject || "";

  useEffect(() => {
    // Hent kontakt-siden fra CMS
    fetch("https://api.dev.auxo.dk/public/by-slug/da/kontakt")
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke hente kontakt-data");
        return res.json();
      })
      .then((data) => {
        setPageData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Fejl ved hentning af kontakt-data:", err);
        setLoading(false);
      });
  }, []);

  // Helpers
  const getVal = (field, defaultVal = "") => {
    if (!field) return defaultVal;
    if (typeof field === "object") {
      return field.da || Object.values(field)[0] || defaultVal;
    }
    return field;
  };

  const getResizedUrl = (url, width = 1200) => {
    if (!url) return "";
    const imgUrl = Array.isArray(url) ? url[0] : url;
    if (!imgUrl.startsWith("https://assets.auxodev.dk")) return imgUrl;
    return imgUrl.replace(
      "https://assets.auxodev.dk/",
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
    );
  };

  // Sæt metadata dynamisk
  useEffect(() => {
    if (pageData) {
      document.title = getVal(pageData.meta_title, "Kontakt - Grønhøjauto v/Mads Kallerup");
      
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute(
        "content",
        getVal(
          pageData.meta_description,
          "Kontakt Grønhøjauto v/Mads Kallerup. Ring på 20 86 16 65, send en mail eller udfyld vores kontaktformular. Vi glæder os til at hjælpe dig!"
        )
      );
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-[120px]">
        <div className="text-center space-y-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-green" />
          <p className="text-stone-500 font-semibold">Indlæser kontaktside...</p>
        </div>
      </div>
    );
  }

  const heroImg = getResizedUrl(
    pageData?.hero_image || "https://assets.auxodev.dk/assets/45330f91-18ad-4301-b0b9-f03458bae5a3/gronhojauto-customer-service.jpg",
    1600
  );

  return (
    <div className="pt-[72px] min-[1200px]:pt-[115px]">
      {/* Hero / Banner */}
      <section className="relative py-20 bg-stone-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImg}
            alt="Grønhøjauto Kontakt"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-900/40 to-stone-950/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center space-y-4">
          <span className="text-xs font-bold tracking-widest text-brand-sage uppercase">
            Tidsbestilling & Spørgsmål
          </span>
          <h1 className="text-3xl font-black tracking-tight sm:text-4xl md:text-5xl text-white">
            {getVal(pageData?.title, "Kontakt os")}
          </h1>
          <p className="mx-auto max-w-2xl text-base text-stone-200 md:text-lg font-medium">
            {getVal(pageData?.subtitle, "Vi står klar til at hjælpe dig – ring, skriv eller kig forbi")}
          </p>
        </div>
      </section>

      {/* Hovedindhold Sektion */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Venstre kolonne: Info & Åbningstider */}
            <div className="lg:col-span-5 space-y-8">
              {/* Brødtekst fra CMS */}
              <div
                className="prose prose-stone max-w-none prose-p:text-stone-600 prose-p:leading-relaxed prose-p:text-base prose-strong:text-brand-stone-dark space-y-4"
                dangerouslySetInnerHTML={{ __html: getVal(pageData?.content) }}
              />

              {/* Kort / Find os */}
              <div className="group rounded-2xl border border-stone-200 overflow-hidden shadow-xs transition-all duration-300 hover:shadow-md hover:border-brand-sage hover:-translate-y-0.5">
                <div className="bg-stone-100 p-4 border-b border-stone-200 flex items-center justify-between transition-colors duration-300 group-hover:bg-stone-50 group-hover:border-brand-sage/20">
                  <span className="text-sm font-bold text-brand-stone-dark">
                    <i className="fa-solid fa-map-location-dot text-brand-sage mr-1.5" /> Find værkstedet i Hurup Thy
                  </span>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      contactInfo.address + ", " + contactInfo.postalCode
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-brand-green hover:text-brand-sage transition"
                  >
                    Åben i Google Maps <i className="fa-solid fa-arrow-up-right-from-square text-[10px] ml-0.5" />
                  </a>
                </div>
                {/* Interaktivt Google Map iframe */}
                <div className="relative w-full h-[350px] bg-stone-50 overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d319881.2743076596!2d7.888488295595798!3d56.801020312077995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46498bc7dd3321cf%3A0x8d1c622e30eb955e!2sGr%C3%B8nh%C3%B8jauto%20v%2FMads%20Kallerup!5e0!3m2!1sen!2sdk!4v1781526676615!5m2!1sen!2sdk"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Grønhøjauto Google Maps"
                    className="w-full h-full"
                  />
                </div>
              </div>
            </div>

            {/* Højre kolonne: Formular */}
            <div className="lg:col-span-7">
              <div className="space-y-4 mb-6">
                <h3 className="text-xl font-black text-brand-stone-dark">
                  Send en besked eller book tid direkte
                </h3>
                <p className="text-sm text-stone-500">
                  Udfyld formularen nedenfor, så vender Mads tilbage til dig med et uforpligtende tilbud eller bekræftelse på din tidsbestilling.
                </p>
              </div>
              <ContactForm initialSubject={initialSubject} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
